import {
  TxAuditRecordModel,
  VisiblAuditFlowModel,
  VisiblAuditRuleModel,
} from '@/services/apis/history/model'
import { Avatar, Button, Popover, Timeline } from 'antd'
import styles from './index.module.scss'
import React, { memo, ReactNode } from 'react'
import { useTranslation } from '@/services/i18n'
import { ApprovalFlowTypeEnum, TxAuditStatusEnum, TxAuditStatusMap } from '@/constants/enum'
import logo from '@/assets/images/safeHeron.png'
import dayjs from 'dayjs'
import cls from 'classnames'

interface ChildProps {
  flow: TxAuditRecordModel
  isCancel?: boolean
  renderChildren?: () => ReactNode
}

const ApprovalFlow = memo<ChildProps>(props => {
  const { t } = useTranslation()
  const { flow, isCancel, renderChildren } = props
  function visbleFlow(): VisiblAuditFlowModel {
    const ruleList: VisiblAuditRuleModel[] = []
    flow?.auditRuleList?.forEach((rule, index) => {
      // 已审批列表
      const operatedList = rule.auditUserList.filter(v => v.auditType !== TxAuditStatusEnum.NEW)
      // 待审批列表
      const doingList = rule.auditUserList.filter(v => v.auditType === TxAuditStatusEnum.NEW)
      rule.totalNum = rule.auditUserList.length
      if (flow.auditStatus === TxAuditStatusEnum.PASSED) {
        // 审批流通过
        rule.auditUserList.forEach(user => {
          if (user.auditType !== TxAuditStatusEnum.NEW) {
            ruleList.push({
              ...rule,
              index,
              auditUser: user,
              auditUserList: [],
            })
          }
        })
      } else if (flow.auditStatus === TxAuditStatusEnum.REFUSED || isCancel) {
        // 审批流拒绝
        if (index <= flow.auditRuleIndex) {
          operatedList.forEach(user => {
            ruleList.push({
              ...rule,
              index,
              auditUser: user,
              auditUserList: [],
            })
          })
        }
      } else {
        // 审批中
        const passedList = rule.auditUserList.filter(v => v.auditType === TxAuditStatusEnum.PASSED)
        if (passedList.length >= rule.num) {
          // 当前节点审批通过
          operatedList.forEach(user => {
            ruleList.push({
              ...rule,
              index,
              auditUser: user,
              auditUserList: [],
            })
          })
        } else {
          // 当前节点审批中
          operatedList.forEach(user => {
            ruleList.push({
              ...rule,
              index,
              auditUser: user,
              auditUserList: [],
            })
          })
          ruleList.push({
            ...rule,
            index,
            auditUser: {} as any,
            auditUserList: doingList,
          })
        }
      }
    })
    return {
      ...flow,
      auditRuleList: ruleList,
    }
  }

  function statusMap(status: TxAuditStatusEnum) {
    return TxAuditStatusMap[status]
  }

  function timeFormat(time: number) {
    return dayjs(time).format('YYYY.MM.DD HH:mm:ss')
  }
  // 发起人
  const createUser = (
    <div className={styles.singleUser}>
      <div className={styles.userStatus}>
        <Avatar size="default" src={visbleFlow().initiatorUserPortrait || logo} />
        <div className={styles.user}>
          <div className={styles.boldText}>{t('record.createUser')}</div>
          <span>{visbleFlow().initiatorUserName}</span>
        </div>
      </div>
      <div>{timeFormat(visbleFlow().initiatorTime)}</div>
    </div>
  )
  // 审批流
  const auditUser = visbleFlow()?.auditRuleList?.map((item, index) => {
    return item.auditUser.auditTime ? (
      <div className={cls(styles.singleUser, styles[`flow-color-${(item.index + 1) % 4}`])}>
        <div className={styles.userStatus}>
          <Avatar size="default" src={item.auditUser.auditUserPortrait} />
          <div className={styles.user}>
            {item.type === ApprovalFlowTypeEnum.SELF ? (
              <div className={styles.boldText}>
                {index === 0 ? t('common.quickAudit') : t('common.InitiatorSelf')}
              </div>
            ) : (
              <div className={styles.boldText}>
                {item.name}({item.num}/{item.totalNum})
              </div>
            )}
            <span>
              {item.auditUser.auditUserName}
              <span
                className={cls({
                  [styles.pending]: item.auditUser.auditType === TxAuditStatusEnum.NEW,
                  [styles.passed]: item.auditUser.auditType === TxAuditStatusEnum.PASSED,
                  [styles.refused]: item.auditUser.auditType === TxAuditStatusEnum.REFUSED,
                  [styles.boldText]: true,
                })}
              >
                ({statusMap(item.auditUser.auditType)})
              </span>
            </span>
          </div>
        </div>
        <div>{timeFormat(item.auditUser.auditTime)}</div>
      </div>
    ) : (
      <div className={cls(styles.mutipleUser, styles[`flow-color-${(item.index + 1) % 4}`])}>
        {item.type === ApprovalFlowTypeEnum.RULE ? (
          <header className={styles.boldText}>
            {item.name} ({item.num}/{item.totalNum})
          </header>
        ) : (
          <header className={styles.boldText}>
            {index === 0 ? t('common.quickAudit') : t('common.InitiatorSelf')}
          </header>
        )}
        <div className={styles.userList}>
          {item.auditUserList.map(user => {
            return (
              <div key={user.auditTime} className={styles.userState}>
                <Avatar size="default" src={user.auditUserPortrait} />
                <div className={styles.user}>
                  <div>{user.auditUserName}</div>
                  {item.index === visbleFlow().auditRuleIndex && (
                    <span className={styles.boldText}>({statusMap(user.auditType)})</span>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  })
  //  dot图标
  const dot = <div className={styles.dot}></div>
  const auditUserChildren = auditUser.map(children => {
    return {
      children,
      dot,
    }
  })
  const items = [
    {
      children: createUser,
      dot: dot,
    },
    ...auditUserChildren,
  ]

  return (
    <div className={styles.approveView}>
      <Popover
        placement="bottom"
        title={<div style={{ fontSize: '14px', color: '#888E8E' }}>{t('record.approval')}</div>}
        content={
          <div>
            <Timeline items={items} />
          </div>
        }
        trigger="hover"
        overlayInnerStyle={{ minWidth: 400, maxHeight: 500, overflowY: 'auto' }}
      >
        {renderChildren ? (
          renderChildren()
        ) : (
          <Button type="link" style={{ fontWeight: '800', marginTop: '-50px' }}>
            {t('common.check')}
          </Button>
        )}
      </Popover>
    </div>
  )
})

export default ApprovalFlow
